import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { DeleteConfirmationComponent } from '../../components/delete-confirmation/delete-confirmation.component';
import { Icons } from '../../../shared/helpers/icons';
import { IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { IHazardousMaterial } from '../../../shared/models/hazardousMaterial';
import { HazardousMaterialsService } from '../../../shared/services/hazardousMaterials.service';
import { HazardousMaterialFormComponent } from './hazardous-material-form/hazardous-material-form.component';
import { NgxPaginationModule } from 'ngx-pagination';

@Component({
  selector: 'hazardous-materials',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    PrvNextButtonsComponent,
    DeleteConfirmationComponent,
    HazardousMaterialFormComponent,
    NgxPaginationModule
  ],
  templateUrl: './hazardous-materials.component.html',
  styleUrl: './hazardous-materials.component.css'
})
export class HazardousMaterialsComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;

  materialsList: IHazardousMaterial[] = [];
  selectedItem: IHazardousMaterial | null = null;

  showForm: boolean = false;
  formItem: IHazardousMaterial | null = null;   // null = add, otherwise edit
  showDeleteConfirmation: boolean = false;

  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string = '';

  // Table sort + pagination
  sortOrder: boolean = true;
  page: number = 1;
  itemsPerPage: number = 10;

  constructor(
    private userDataService: UserDataService,
    private hazardousMaterialsService: HazardousMaterialsService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadMaterials();
  }

  loadMaterials(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.hazardousMaterialsService.getHazardousMaterials(this.currentBuildingId).subscribe({
      next: (response) => {
        this.loading = false;
        this.materialsList = response && response.data ? response.data : [];
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  // Sort the list by a column when its header is clicked.
  sortList(key: keyof IHazardousMaterial): void {
    this.sortOrder = !this.sortOrder;
    this.materialsList.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';
      if (valueA < valueB) return this.sortOrder ? -1 : 1;
      if (valueA > valueB) return this.sortOrder ? 1 : -1;
      return 0;
    });
  }

  openAdd(): void {
    this.formItem = null;
    this.showForm = true;
  }

  openEdit(item: IHazardousMaterial): void {
    this.formItem = item;
    this.showForm = true;
  }

  closeForm(): void {
    this.showForm = false;
    this.formItem = null;
  }

  handleSaved(message: { type: string, message: string }): void {
    this.closeForm();
    this.loadMaterials();
    this.messageModal = message.type;
    this.popupMessage = message.message;
  }

  openDelete(item: IHazardousMaterial): void {
    this.selectedItem = item;
    this.showDeleteConfirmation = true;
  }

  closeDelete(): void {
    this.showDeleteConfirmation = false;
    this.selectedItem = null;
  }

  handleDelete(item: IHazardousMaterial): void {
    this.showDeleteConfirmation = false;
    if (!this.currentBuildingId || !item?.id) return;
    this.loading = true;
    this.hazardousMaterialsService.deleteHazardousMaterial(this.currentBuildingId, String(item.id)).subscribe({
      next: () => {
        this.selectedItem = null;
        this.loading = false;
        this.messageModal = 'success';
        this.popupMessage = 'This occurred after deleting a hazardous material.';
        this.loadMaterials();
      },
      error: (error: any) => {
        this.selectedItem = null;
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
}
